import { z } from 'zod'

import { DEFAULT_REPORT_OUTLINE } from './reportOutline'

export const SECTION_CATALOG_VERSION = '1.0.0'

export const REPORT_SECTION_IDS = [
  'introduction',
  'what-we-looked-for',
  'quick-picks',
  'products',
  'video-insights',
  'social-insights',
  'how-to-choose',
  'evidence-snapshot',
  'our-recommendation',
  'final-recommendations',
  'sources',
] as const

export const ReportSectionIdSchema = z.enum(REPORT_SECTION_IDS)
export type ReportSectionId = z.infer<typeof ReportSectionIdSchema>

export type ReportSectionStrategy = 'deterministic' | 'llm'

export const ReportSectionTriggerSchema = z
  .object({
    /**
     * Top-level artifact keys in the sandbox->API ingestion payload.
     *
     * These keys map directly to fields in the `IngestArtifacts` patch the API
     * receives (e.g. `structuredData`, `videoInsightsData`).
     */
    artifactKeys: z.array(z.string()).optional(),

    /**
     * Nested keys inside `reportArtifacts` (brief/article JSON emitted by the
     * sandbox pipeline).
     *
     * Example: ["brief"] or ["methodology"].
     */
    reportArtifactsKeys: z.array(z.string()).optional(),
  })
  .strict()

export type ReportSectionTrigger = z.infer<typeof ReportSectionTriggerSchema>

export const ReportSectionDefinitionSchema = z
  .object({
    id: ReportSectionIdSchema,
    title: z.string(),
    intent: z.string().optional(),

    /**
     * Whether the report generator treats this section as required when an LLM
     * proposes a custom outline.
     */
    required: z.boolean().optional(),

    /**
     * High-level generation strategy used for this section.
     * This is metadata only in the shared catalog; runtime logic lives in API.
     */
    strategy: z.enum(['deterministic', 'llm']).optional(),

    /**
     * Coarse trigger map from incoming artifact updates -> report sections.
     * Used by the API to decide which sections to regenerate incrementally.
     */
    triggers: z.array(ReportSectionTriggerSchema).optional(),
  })
  .strict()

export type ReportSectionDefinition = z.infer<
  typeof ReportSectionDefinitionSchema
>

const DEFAULT_SECTION_META = new Map(
  DEFAULT_REPORT_OUTLINE.map((section) => [
    section.id,
    {
      title: section.title,
      intent: section.intent,
    },
  ])
)

export const REPORT_SECTION_CATALOG: ReadonlyArray<ReportSectionDefinition> =
  ReportSectionIdSchema.options.map((id) => {
    const meta = DEFAULT_SECTION_META.get(id)
    if (!meta) {
      throw new Error(`Missing default title/intent for report section "${id}"`)
    }

    const base: ReportSectionDefinition = {
      id,
      title: meta.title,
      intent: meta.intent,
    }

    // Metadata + coarse triggers (can evolve without breaking templates).
    switch (id) {
      case 'introduction':
        return {
          ...base,
          required: true,
          strategy: 'llm',
          triggers: [
            { artifactKeys: ['briefMd'] },
            { reportArtifactsKeys: ['brief'] },
          ],
        }
      case 'what-we-looked-for':
        return {
          ...base,
          required: true,
          strategy: 'deterministic',
          triggers: [
            { reportArtifactsKeys: ['brief'] },
            { reportArtifactsKeys: ['methodology'] },
            { artifactKeys: ['briefMd'] },
          ],
        }
      case 'products':
        return {
          ...base,
          required: true,
          strategy: 'deterministic',
          triggers: [{ artifactKeys: ['structuredData'] }],
        }
      case 'video-insights':
        return {
          ...base,
          strategy: 'deterministic',
          triggers: [{ artifactKeys: ['videoInsightsData'] }],
        }
      case 'social-insights':
        return {
          ...base,
          strategy: 'deterministic',
          triggers: [{ artifactKeys: ['socialInsightsData'] }],
        }
      case 'evidence-snapshot':
        return {
          ...base,
          strategy: 'llm',
          triggers: [
            { reportArtifactsKeys: ['rankings'] },
            { artifactKeys: ['structuredData'] },
            { artifactKeys: ['videoInsightsData', 'socialInsightsData'] },
            { artifactKeys: ['articleMd'] },
          ],
        }
      case 'our-recommendation':
        return {
          ...base,
          strategy: 'llm',
          triggers: [
            { reportArtifactsKeys: ['rankings'] },
            { artifactKeys: ['structuredData'] },
            { artifactKeys: ['articleMd'] },
          ],
        }
      case 'quick-picks':
      case 'how-to-choose':
        return {
          ...base,
          strategy: 'llm',
          // Ranking is the primary trigger; articleMd is a late-arriving
          // enrichment fallback that can refine copy when available.
          triggers: [
            { reportArtifactsKeys: ['rankings'] },
            { artifactKeys: ['structuredData'] },
            { artifactKeys: ['articleMd'] },
          ],
        }
      case 'final-recommendations':
        return {
          ...base,
          required: true,
          strategy: 'llm',
          // Verdict runs from ranking data first, then can refresh
          // when articleMd is available as enrichment.
          triggers: [
            { reportArtifactsKeys: ['rankings'] },
            { artifactKeys: ['structuredData'] },
            { artifactKeys: ['articleMd'] },
          ],
        }
      case 'sources':
        return {
          ...base,
          required: true,
          strategy: 'deterministic',
          triggers: [
            {
              artifactKeys: [
                'structuredData',
                'candidatesPoolJson',
                'candidatesJson',
              ],
            },
          ],
        }
      default:
        return base
    }
  })

export function getReportSectionDefinition(
  id: string
): ReportSectionDefinition | undefined {
  const canonical = id as ReportSectionId
  return REPORT_SECTION_CATALOG.find((section) => section.id === canonical)
}

export function listReportSectionIds(): ReportSectionId[] {
  return [...ReportSectionIdSchema.options]
}

export function listRequiredReportSections(): ReportSectionId[] {
  return REPORT_SECTION_CATALOG.filter((section) => section.required).map(
    (section) => section.id
  )
}
